import { useLingui } from "@lingui/react/macro";
import { ChevronRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/shadcn-ui";

import {
  findActiveItem,
  isUnder,
  MenuItem,
  useActivePathname,
} from "../menu/menu-list";

export type LegacyMenuVariant = "sidebar" | "sheet";

/**
 * The pre-redesign menu renderer, kept for the legacy layout. Reads the same
 * `buildMenu` tree as the new sidebar (ignoring its icons), so a page added to
 * the nav shows up in both layouts. Recurses for nested items.
 */
export function LegacyMenuList({
  items,
  variant,
  level = 0,
  activeItem,
}: {
  items: MenuItem[];
  variant: LegacyMenuVariant;
  level?: number;
  activeItem?: MenuItem;
}) {
  const activePathname = useActivePathname();
  const visible = items.filter((item) => item.show !== false);
  if (visible.length === 0) {
    return null;
  }

  // Decide the single highlighted item ourselves (deepest path match) so a child
  // sharing its parent's `to` wins. Computed once at the root, then threaded down.
  const active = activeItem ?? findActiveItem(items, activePathname);

  return (
    <div className="flex flex-col">
      {visible.map((item) => (
        <LegacyMenuListItem
          key={`${item.to}:${item.label}`}
          item={item}
          variant={variant}
          level={level}
          activeItem={active}
        />
      ))}
    </div>
  );
}

/** The submenu of the active top-level section, for the desktop sidebar. */
export function getActiveSubmenu(
  menu: MenuItem[],
  pathname: string,
): MenuItem[] | null {
  const section = menu.find(
    (item) => item.children && isUnder(item.to, pathname),
  );
  return section?.children ?? null;
}

function LegacyMenuListItem({
  item,
  variant,
  level,
  activeItem,
}: {
  item: MenuItem;
  variant: LegacyMenuVariant;
  level: number;
  activeItem?: MenuItem;
}) {
  const activePathname = useActivePathname();
  const { t } = useLingui();
  const childItems = (item.children ?? []).filter((c) => c.show !== false);
  const expandable = childItems.length > 0;
  const containsActiveRoute = expandable && isUnder(item.to, activePathname);
  const [expanded, setExpanded] = useState(
    item.defaultExpanded ?? containsActiveRoute,
  );

  // Auto-expand when the active route falls under this item. Not keyed on
  // `expanded`, so a manual collapse isn't immediately undone — it only
  // re-expands when the active route changes.
  useEffect(() => {
    if (containsActiveRoute) {
      setExpanded(true);
    }
  }, [containsActiveRoute]);

  const toggleExpanded = () => setExpanded((value) => !value);

  // Highlight the current item, or a collapsed ancestor that hides it.
  const isCurrent = item === activeItem;
  const selected = isCurrent || (containsActiveRoute && !expanded);

  // Top-level rows are emphasised only in the sheet, where they're the main nav;
  // in the sidebar they're a section's page items, so they stay regular weight.
  const isSheet = variant === "sheet";
  const top = level === 0 && isSheet;
  // The selected pill differs by variant: a faint grey on the drawer's white,
  // but white on the sidebar's grey (grey-on-grey wouldn't read).
  const selectedPill = isSheet ? "bg-[var(--menu-selected)]" : "bg-background";

  return (
    <>
      <div className="flex items-stretch pl-3 pr-2">
        {Array.from({ length: level }, (_, i) => (
          <div
            key={i}
            className="w-4 flex-none self-stretch border-l border-[var(--menu-rail)]"
          />
        ))}
        {/* The pill (hover/selected background) wraps the link and caret as
         * siblings — a <button> can't live inside an <a>. */}
        <div
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 rounded-md px-3",
            isSheet ? "hover:bg-accent" : "hover:bg-background",
            selected && selectedPill,
          )}
        >
          <Link
            to={item.to}
            aria-current={isCurrent ? "page" : undefined}
            className={cn(
              "min-w-0 flex-1 truncate py-1.5 text-base text-foreground hover:no-underline",
              top && "py-2.5",
              (top || selected) && "font-semibold",
            )}
          >
            {item.label}
          </Link>
          {/* Outside the link, so the dot doesn't end up in its accessible name. */}
          {item.dot && (
            <span
              role="img"
              aria-label={item.dot}
              title={item.dot}
              className="size-2 flex-none rounded-full bg-brand-primary"
            />
          )}
          {item.count && (
            <span
              role="img"
              aria-label={item.count.label}
              title={item.count.label}
              className="inline-flex h-4 min-w-4 flex-none items-center justify-center rounded-full bg-brand-primary px-1 text-2xs font-bold text-brand-primary-foreground"
            >
              {item.count.value}
            </span>
          )}
          {expandable && (
            <button
              type="button"
              className="inline-flex flex-none cursor-pointer items-center justify-center border-none bg-transparent p-0.5 text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={toggleExpanded}
              aria-label={expanded ? t`Collapse` : t`Expand`}
            >
              <ChevronRightIcon
                className={`size-4 transition-transform ${
                  expanded ? "rotate-90" : ""
                }`}
              />
            </button>
          )}
        </div>
      </div>
      {expandable && expanded && (
        <LegacyMenuList
          items={childItems}
          variant={variant}
          level={level + 1}
          activeItem={activeItem}
        />
      )}
    </>
  );
}
